import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { FaUsers, FaFilePdf, FaPlus, FaBuilding, FaClipboardList, FaMapMarker } from 'react-icons/fa'; //, FaEye, FaMapMarker, FaList, FaTrash, FaTimes
import { getProjectMembers, getActiveProject } from '@/utils/all_apis/project'; //addMemberToProject, deleteMemberFromProject, getAllProjects
import { getInspections, getSites } from '@/utils/all_apis/admin'; //deleteInspectionType,, getInspectionTypes, getSites
import Link from 'next/link';
import { FaArrowRight } from "react-icons/fa";
import { adminReportUrls } from '@/app/(main)/report/components/ReportData';


interface InspectionType {
  id: any;
  type: string;
}

interface Inspection {
  id: number;
  status: string;
  date: string;
  inspectionSite: any;
  description: string;
  inspector: any;
  unique_id: any;
  user_id:any;
  inspectionType: InspectionType
}

interface Site {
  id: number;
  name: string;
  location: string;
}

interface Member {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
}

const statusColors: { [key: string]: string } = {
  "Closed": 'bg-gray-400',
  'Pending': 'bg-yellow-400',
  "Approved": 'bg-green-400',
  "Rejected": 'bg-red-400',
  'Awaiting Approval': 'bg-orange-400',
  'On Hold': 'bg-blue-400',
  'Started': 'bg-green-400'
};

const defaultColor = 'bg-gray-500'; // Default color for unknown statuses

const getStatusColor = (status: string): string => {
  return statusColors[status] || defaultColor;
};


const AdminDashboard: React.FC = () => {
  const router = useRouter();
  const [members, setMembers] = useState<Member[]>([]);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {

      const activeProject = await getActiveProject()
      setSelectedProject(activeProject)

      const projectMembers = await getProjectMembers(activeProject?.id);
      setMembers(projectMembers);

      const fetchedInspections = await getInspections();
      setInspections(fetchedInspections);

      const fetchedSites = await getSites();
      setSites(fetchedSites);
    };

    fetchData();
  }, []);

  const handleAddSite = () => {
    router.push('/all-sites');
  };

  const handleAddInspectionType = () => {
    router.push('/all-inspection-types');
  };

  return (
    <div className="flex flex-col md:flex-row p-4">
      {/* Left Half: Sites, Inspection Types, Users and Reports */}
      <div className="w-full md:w-1/2 p-4">

        <Link href={`/reports`}><h1 className="text-2xl font-bold mt-8 mb-4 hover:underline text-blue-500 hover:text-blue-600">Reports</h1></Link>
        <ul className="space-y-4">
          {Object.keys(adminReportUrls).map((report, index) => (
            <Link key={index} href={adminReportUrls[report]}>
              <li className="hover:bg-gray-50 hover:cursor-pointer bg-white p-4 rounded shadow flex items-center">
                <FaFilePdf className="mr-2 text-gray-500" />
                <span>{report}</span>
              </li>
            </Link>
          ))}
        </ul>

        <Link href={`${selectedProject?"/project?id="+selectedProject.id:""}`}><h1 className="text-2xl font-bold mt-8 mb-4 hover:underline text-blue-500 hover:text-blue-600">Users</h1></Link>
        {members.length === 0 ? (
          <p className="text-gray-500">No users available.</p>
        ) : (
          <ul className="space-y-4">
            {members.map((user) => (
              <li key={user.id} className="bg-white p-4 rounded shadow flex items-center justify-between">
                <div className="flex items-center">
                  <FaUsers className="mr-2 text-gray-500" />
                  <div>
                    <h2 className="text-xl font-bold">{user.email}</h2>
                    <p className="text-gray-600">{user.role}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right Half: Add Site, Add Inspection Type, Inspections */}
      <div className="w-full md:w-1/2 p-4 bg-gray-100">
        <h1 className="text-2xl font-bold mb-4">Admin Actions</h1>
        <div className="bg-white p-4 rounded shadow mb-4">
          <Link href={`/dashboard`}>
            <h2 className="flex items-center text-xl font-bold mt-4 mb-4 hover:underline text-blue-500 hover:text-blue-600">
            <FaArrowRight className="mr-2" /> Analytics
            </h2>
          </Link>
        </div>
        <div className="bg-white p-4 rounded shadow mb-4">
          <h2 className="text-xl font-bold mb-2 flex items-center">
            <FaPlus className="mr-2" /> Add Site
          </h2>
          <button
            onClick={handleAddSite}
            className="bg-blue-500 text-white px-4 py-2 rounded mb-4 flex items-center"
          >
            <FaBuilding className="mr-2" /> Add Site
          </button>
          
        </div>

        <div className="bg-white p-4 rounded shadow mb-4">
          <h2 className="text-xl font-bold mb-2 flex items-center">
            <FaPlus className="mr-2" /> Add Inspection Type
          </h2>
          <button
            onClick={handleAddInspectionType}
            className="bg-blue-500 text-white px-4 py-2 rounded mb-4 flex items-center"
          >
            <FaClipboardList className="mr-2" /> Add Inspection Type
          </button>
        </div>

        <Link href={`/all-inspections`}><h1 className="text-2xl font-bold mt-8 mb-4 hover:underline text-blue-500 hover:text-blue-600">Inspections</h1></Link>
        {inspections.length === 0 ? (
          <p className="text-gray-500">No inspections available.</p>
        ) : (
          <ul className="space-y-4">
            {inspections.map((inspection) => (
              <li key={inspection.id} className="bg-white p-4 rounded shadow">
                <Link href={`/inspection-detail?id=${inspection.id}`} className="text-blue-500 hover:underline">
                  <h2 className="text-xl font-bold">
                  {inspection.inspectionType.type}{inspection?.inspectionSite?.siteName?" at "+inspection?.inspectionSite?.siteName:""}
                    {/* {inspection?.inspectionType?.type} / {"IN-"+formatNumber(inspection.unique_id, inspection.user_id)} */}
                    </h2>
                </Link>
                <div className={`flex items-center justify-between p-2 ${getStatusColor(inspection.status)} text-white`}>
                  <div className="text-lg font-bold">{inspection.status}</div>
                </div>
                <div className="mt-2">
                  <p><strong>Inspector:</strong> {inspection?.inspector?.firstName ? inspection.inspector.firstName + " "+ inspection?.inspector?.lastName: inspection?.inspector?.emailId}</p>
                  <p><strong>Date:</strong> {inspection.date}</p>
                  <p><strong>Site:</strong> {inspection?.inspectionSite?.siteName}</p>
                  {inspection.description && <p><strong>Description:</strong> {inspection.description}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <Link href={`/all-sites`}><h1 className="text-2xl font-bold mt-8 mb-4 hover:underline text-blue-500 hover:text-blue-600">Sites</h1></Link>
        {sites.length === 0 ? (
          <p className="text-gray-500">No sites available.</p>
        ) : (
          <ul className="space-y-4">
            {sites.map((site) => (
              <li key={site.id} className="bg-white p-4 rounded shadow flex items-center justify-between">
                <div className="flex items-center">
                  <FaMapMarker className="mr-2 text-gray-500" />
                  <div>
                    <h2 className="text-xl font-bold">{site.name}</h2>
                    <p className="text-gray-600">{site.location}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;